import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createChart } from "lightweight-charts";
import {
  CHART_INTERACTION_DEFAULTS,
  attachChartWheelPan,
  chartLocalizationForPriceScale,
  crosshairModeFromValue,
  priceScaleModeFromValue,
} from "./chartDisplayConstants";
import { addMainPriceSeries, applyMainPriceSeriesColors } from "./chartPriceSeries";
import {
  DEFAULT_CHART_TIME_ZONE,
  formatChartTickMark,
  formatChartTooltipTime,
} from "./chartTimeZoneUtils";

/**
 * Duplicate main price series for multi-pane layouts (same transformed data as primary chart).
 */
export default function ChartMirrorPane({
  priceChartPayload,
  priceSeriesStyle,
  chartBackgroundColor,
  bullishCandleBodyColor,
  bearishCandleBodyColor,
  bullishCandleBorderColor,
  bearishCandleBorderColor,
  bullishCandleWickColor,
  bearishCandleWickColor,
  shellText,
  gridLine,
  scaleBorder,
  crosshairVert,
  crosshairHorz,
  chartCrosshairMode = "normal",
  mainPriceScaleMode = "normal",
  chartTimeZone = DEFAULT_CHART_TIME_ZONE,
  yAxisSide = "right",
  fitKey = "",
}) {
  const [containerEl, setContainerEl] = useState(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  /** fitKey + whether data was present at the last fit; refit only when either changes. */
  const lastFitRef = useRef(null);

  const colorBundle = {
    bullishCandleBodyColor,
    bearishCandleBodyColor,
    bullishCandleBorderColor,
    bearishCandleBorderColor,
    bullishCandleWickColor,
    bearishCandleWickColor,
    chartBackgroundColor,
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useLayoutEffect(() => {
    if (!containerEl) {
      return undefined;
    }

    const width = Math.max(1, containerEl.clientWidth);
    const height = Math.max(1, containerEl.clientHeight);

    chartRef.current = createChart(containerEl, {
      width,
      height,
      ...CHART_INTERACTION_DEFAULTS,
      layout: {
        background: { color: chartBackgroundColor },
        textColor: shellText,
        fontFamily: '"Avenir Next", "Trebuchet MS", sans-serif',
        attributionLogo: true,
      },
      crosshair: {
        mode: crosshairModeFromValue(chartCrosshairMode),
        vertLine: { color: crosshairVert, width: 1 },
        horzLine: { color: crosshairHorz, width: 1 },
      },
      grid: {
        vertLines: { color: gridLine },
        horzLines: { color: gridLine },
      },
      rightPriceScale: {
        visible: yAxisSide === "right",
        borderColor: scaleBorder,
        mode: priceScaleModeFromValue(mainPriceScaleMode),
      },
      leftPriceScale: {
        visible: yAxisSide === "left",
        borderColor: scaleBorder,
        mode: priceScaleModeFromValue(mainPriceScaleMode),
      },
      localization: {
        timeFormatter: (businessDayOrTimestamp) =>
          formatChartTooltipTime(businessDayOrTimestamp, chartTimeZone),
        ...chartLocalizationForPriceScale(mainPriceScaleMode),
      },
      timeScale: {
        borderColor: scaleBorder,
        timeVisible: true,
        secondsVisible: false,
        barSpacing: 5,
        minBarSpacing: 0.65,
        tickMarkFormatter: (time, tickMarkType, locale) =>
          formatChartTickMark(time, tickMarkType, locale, chartTimeZone),
      },
    });

    const detachChartWheel = attachChartWheelPan(() => chartRef.current, containerEl);

    seriesRef.current = addMainPriceSeries(
      chartRef.current,
      priceSeriesStyle,
      priceChartPayload,
      colorBundle
    );
    seriesRef.current.setData(priceChartPayload.data);
    seriesRef.current.setSeriesOrder(0);

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      const w = entry?.contentRect?.width;
      const h = entry?.contentRect?.height;
      if (!chartRef.current || !Number.isFinite(w) || !Number.isFinite(h)) {
        return;
      }
      chartRef.current.applyOptions({
        width: Math.max(1, w),
        height: Math.max(1, h),
      });
    });
    resizeObserver.observe(containerEl);

    return () => {
      detachChartWheel();
      resizeObserver.disconnect();
      chartRef.current?.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, [containerEl, priceSeriesStyle]);
  /* eslint-enable react-hooks/exhaustive-deps */

  /* eslint-disable react-hooks/exhaustive-deps -- colorBundle mirrors listed fields */
  useEffect(() => {
    if (!seriesRef.current) {
      return;
    }
    applyMainPriceSeriesColors(
      seriesRef.current,
      priceSeriesStyle,
      colorBundle,
      priceChartPayload
    );
  }, [
    bearishCandleBodyColor,
    bearishCandleBorderColor,
    bearishCandleWickColor,
    bullishCandleBodyColor,
    bullishCandleBorderColor,
    bullishCandleWickColor,
    priceChartPayload,
    priceSeriesStyle,
  ]);
  /* eslint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    chartRef.current?.applyOptions({
      ...CHART_INTERACTION_DEFAULTS,
      layout: {
        background: { color: chartBackgroundColor },
        textColor: shellText,
      },
      grid: {
        vertLines: { color: gridLine },
        horzLines: { color: gridLine },
      },
      rightPriceScale: {
        visible: yAxisSide === "right",
        borderColor: scaleBorder,
        mode: priceScaleModeFromValue(mainPriceScaleMode),
      },
      leftPriceScale: {
        visible: yAxisSide === "left",
        borderColor: scaleBorder,
        mode: priceScaleModeFromValue(mainPriceScaleMode),
      },
      localization: {
        timeFormatter: (businessDayOrTimestamp) =>
          formatChartTooltipTime(businessDayOrTimestamp, chartTimeZone),
        ...chartLocalizationForPriceScale(mainPriceScaleMode),
      },
      timeScale: {
        borderColor: scaleBorder,
        tickMarkFormatter: (time, tickMarkType, locale) =>
          formatChartTickMark(time, tickMarkType, locale, chartTimeZone),
      },
      crosshair: {
        mode: crosshairModeFromValue(chartCrosshairMode),
        vertLine: { color: crosshairVert },
        horzLine: { color: crosshairHorz },
      },
    });
  }, [
    chartBackgroundColor,
    chartCrosshairMode,
    crosshairHorz,
    crosshairVert,
    gridLine,
    mainPriceScaleMode,
    scaleBorder,
    shellText,
    chartTimeZone,
    yAxisSide,
  ]);

  useEffect(() => {
    if (!seriesRef.current) {
      return;
    }
    seriesRef.current.setData(priceChartPayload.data);
    // Fit when the pane gets new data (symbol/timeframe change or first load),
    // but not on every live tick, so the user's zoom and scroll are kept.
    const fitState = `${fitKey}|${priceChartPayload.data.length > 0}`;
    if (lastFitRef.current !== fitState) {
      lastFitRef.current = fitState;
      chartRef.current?.timeScale().fitContent();
    }
  }, [priceChartPayload, fitKey]);

  return (
    <div
      ref={setContainerEl}
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        minHeight: 0,
      }}
    />
  );
}
